import { useState } from 'preact/hooks';
import './comment-form.css';
import { User } from '@novelizing/types';
import { Link } from 'wouter';

interface CommentFormProps {
  user?: User;
  loading: boolean;
  onSubmit: (content: string) => Promise<void>; // Renamed from addComment for clarity
  placeholder?: string;
  buttonText?: string;
}

export const CommentForm = ({
  user,
  loading,
  onSubmit,
  placeholder = 'Write a comment...',
  buttonText = 'Post Comment',
}: CommentFormProps) => {
  const [newComment, setNewComment] = useState('');

  if (!user) {
    return (
      <div className="login-prompt">
        <p>
          <a href="/register">Please register</a> or <a href="/login">log in</a>{' '}
          to post comments.
        </p>
        <p>
          Creating an account directly supports the author of this book by
          allowing your chapter views and comments to contribute towards their
          site rankings.
        </p>
        <p>
          It's free, we'll bookmark your place for you, and we'll give you
          macguffins!
        </p>
        <Link role="button" href="/register">
          Register
        </Link>
        <Link role="button" href="/login">
          Log in
        </Link>
      </div>
    );
  }

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const trimmedComment = newComment.trim();
    if (!trimmedComment || !user?.id) return;

    try {
      await onSubmit(trimmedComment);
      setNewComment(''); // Clear form only on successful submission
    } catch (error) {
      console.error('Failed to submit comment:', error);
      // Optionally: display an error message to the user within the form
    }
  };

  return (
    <form onSubmit={handleSubmit} className="comment-form">
      <textarea
        value={newComment}
        onChange={(e) => setNewComment(e.currentTarget.value)}
        onKeyUp={(e) => setNewComment(e.currentTarget.value)} // Keep for consistency, or potentially remove if onChange covers all cases
        placeholder={placeholder}
        rows={3}
        required
        disabled={loading} // Disable textarea while loading
      />
      <button type="submit" disabled={loading || !newComment.trim()}>
        {loading ? 'Posting...' : buttonText}
      </button>
    </form>
  );
};
